import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type SettingsState,
} from '@triedge/staff-settings-domain';
import {
  useGetSettingsModuleConfigQuery,
  useUpdateSettingsModuleConfigMutation,
} from '../../../state/domainRouterApi';
import {
  cloneSettingsState,
  parseSettingsState,
} from '../../../settings/settingsState';

const SETTINGS_MODULE_KEY = 'staff_settings';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const MAX_TECHNICAL_MESSAGE_LENGTH = 800;

export type StaffSettingsSaveError = {
  userMessage: string;
  technicalMessage: string;
};

type RemoteSettingsSnapshot = {
  state: SettingsState;
  version: number | null;
  updatedAt: number | null;
  cacheToken: string;
};

const toFiniteNumberOrNull = (value: unknown): number | null => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildRemoteSettingsSnapshot = (source: unknown): RemoteSettingsSnapshot => {
  const record = asRecord(source);
  const version = toFiniteNumberOrNull(record.version);
  const updatedAt = toFiniteNumberOrNull(record.updatedAt);
  const payload = record.payload;
  return {
    state: parseSettingsState(payload),
    version,
    updatedAt,
    cacheToken: JSON.stringify([version, updatedAt, payload ?? null]),
  };
};

const isSnapshotNewerThan = (
  candidate: RemoteSettingsSnapshot,
  current: RemoteSettingsSnapshot,
): boolean => {
  if (candidate.version !== null && current.version !== null) {
    if (candidate.version !== current.version) {
      return candidate.version > current.version;
    }
  }
  if (candidate.updatedAt !== null && current.updatedAt !== null) {
    return candidate.updatedAt > current.updatedAt;
  }
  return false;
};

const shouldApplyRemoteSnapshot = (
  candidate: RemoteSettingsSnapshot,
  current: RemoteSettingsSnapshot,
): boolean => {
  if (candidate.cacheToken === current.cacheToken) {
    return false;
  }
  if (isSnapshotNewerThan(candidate, current)) {
    return true;
  }
  if (candidate.version === null || current.version === null) {
    return true;
  }
  if (candidate.version === current.version) {
    return candidate.updatedAt === null || current.updatedAt === null || candidate.updatedAt >= current.updatedAt;
  }
  return false;
};

const sanitizeTechnicalMessage = (value: string): string => {
  const redacted = value
    .replace(/("(?:authorization|access[_-]?token|id[_-]?token|refresh[_-]?token|password|secret|api[_-]?key)"\s*:\s*")[^"]+"/gi, '$1[redacted]"')
    .replace(/((?:authorization|access[_-]?token|id[_-]?token|refresh[_-]?token|password|secret|api[_-]?key)\s*[:=]\s*)[^\s,}]+/gi, '$1[redacted]');
  if (redacted.length <= MAX_TECHNICAL_MESSAGE_LENGTH) {
    return redacted;
  }
  return `${redacted.slice(0, MAX_TECHNICAL_MESSAGE_LENGTH)}...`;
};

const readErrorMessage = (error: unknown): string => {
  if (!error) return 'Unknown settings save error.';
  if (error instanceof Error) return sanitizeTechnicalMessage(error.message);
  if (typeof error === 'string') return sanitizeTechnicalMessage(error);
  if (typeof error !== 'object') return String(error);

  const record = error as Record<string, unknown>;
  const data = asRecord(record.data);
  const message = data.message ?? record.message ?? record.error ?? record.statusText;
  if (typeof message === 'string' && message.trim()) {
    return sanitizeTechnicalMessage(message.trim());
  }

  try {
    return sanitizeTechnicalMessage(JSON.stringify(error));
  } catch {
    return 'Unknown settings save error.';
  }
};

const toSettingsSaveError = (error: unknown): StaffSettingsSaveError => ({
  userMessage:
    "We couldn't save the latest settings. Your previous settings are still in place. Please try again, or contact Intellio Engineering at +1-917-753-7754.",
  technicalMessage: readErrorMessage(error),
});

const buildTopLevelPatch = (previous: SettingsState, next: SettingsState): Record<string, unknown> => {
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    const previousValue = (previous as Record<string, unknown>)[key];
    const nextValue = (next as Record<string, unknown>)[key];
    if (JSON.stringify(previousValue) !== JSON.stringify(nextValue)) {
      patch[key] = nextValue;
    }
  }
  return patch;
};

export const useStaffSettingsState = () => {
  const settingsQuery = useGetSettingsModuleConfigQuery({ moduleKey: SETTINGS_MODULE_KEY });
  const [updateSettingsConfig, updateState] = useUpdateSettingsModuleConfigMutation();

  const remoteSnapshot = useMemo(
    () => buildRemoteSettingsSnapshot(settingsQuery.data),
    [settingsQuery.data],
  );

  const [settingsState, setSettingsState] = useState<SettingsState>(remoteSnapshot.state);
  const [saveError, setSaveError] = useState<StaffSettingsSaveError | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [pendingSaveCount, setPendingSaveCount] = useState(0);

  const latestStateRef = useRef<SettingsState>(remoteSnapshot.state);
  const latestRemoteSnapshotRef = useRef<RemoteSettingsSnapshot>(remoteSnapshot);
  const deferredRemoteSnapshotRef = useRef<RemoteSettingsSnapshot | null>(null);
  const pendingSaveCountRef = useRef(0);
  const persistQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const applyRemoteSnapshot = useCallback((snapshot: RemoteSettingsSnapshot) => {
    latestRemoteSnapshotRef.current = snapshot;
    latestStateRef.current = snapshot.state;
    setSettingsState(snapshot.state);
  }, []);

  const flushDeferredRemoteSnapshot = useCallback(() => {
    if (pendingSaveCountRef.current > 0) {
      return;
    }
    const deferredSnapshot = deferredRemoteSnapshotRef.current;
    if (!deferredSnapshot) {
      return;
    }
    deferredRemoteSnapshotRef.current = null;
    if (shouldApplyRemoteSnapshot(deferredSnapshot, latestRemoteSnapshotRef.current)) {
      applyRemoteSnapshot(deferredSnapshot);
    }
  }, [applyRemoteSnapshot]);

  useEffect(() => {
    if (pendingSaveCountRef.current > 0) {
      deferredRemoteSnapshotRef.current = remoteSnapshot;
      return;
    }
    const deferredSnapshot = deferredRemoteSnapshotRef.current;
    if (
      deferredSnapshot
      && shouldApplyRemoteSnapshot(deferredSnapshot, latestRemoteSnapshotRef.current)
    ) {
      deferredRemoteSnapshotRef.current = null;
      applyRemoteSnapshot(deferredSnapshot);
      return;
    }
    deferredRemoteSnapshotRef.current = null;
    if (shouldApplyRemoteSnapshot(remoteSnapshot, latestRemoteSnapshotRef.current)) {
      applyRemoteSnapshot(remoteSnapshot);
    }
  }, [applyRemoteSnapshot, pendingSaveCount, remoteSnapshot]);

  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  const persistPatch = useCallback(
    (patcher: (current: SettingsState) => SettingsState) => {
      const run = async () => {
        const currentState = cloneSettingsState(latestStateRef.current);
        const nextState = patcher(cloneSettingsState(currentState));
        const patch = buildTopLevelPatch(currentState, nextState);
        latestStateRef.current = nextState;
        setSettingsState(nextState);
        setSaveError(null);
        if (!Object.keys(patch).length) {
          return nextState;
        }

        pendingSaveCountRef.current += 1;
        setPendingSaveCount((count) => count + 1);
        try {
          const response = await updateSettingsConfig({
            moduleKey: SETTINGS_MODULE_KEY,
            patch,
          }).unwrap();
          const responseRecord = asRecord(response);
          const responseSnapshot = responseRecord.payload === undefined
            ? {
                state: nextState,
                version: toFiniteNumberOrNull(responseRecord.version),
                updatedAt: toFiniteNumberOrNull(responseRecord.updatedAt),
                cacheToken: JSON.stringify([
                  toFiniteNumberOrNull(responseRecord.version),
                  toFiniteNumberOrNull(responseRecord.updatedAt),
                  nextState,
                ]),
              }
            : buildRemoteSettingsSnapshot(responseRecord);
          const reconciledState = responseRecord.payload === undefined
            ? nextState
            : responseSnapshot.state;
          latestRemoteSnapshotRef.current = responseSnapshot;
          latestStateRef.current = reconciledState;
          setSettingsState(reconciledState);
          setLastSavedAt(new Date().toISOString());
          return reconciledState;
        } catch (error) {
          latestStateRef.current = currentState;
          setSettingsState(currentState);
          setLastSavedAt(null);
          setSaveError(toSettingsSaveError(error));
          throw error;
        } finally {
          pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current - 1);
          setPendingSaveCount((count) => Math.max(0, count - 1));
          flushDeferredRemoteSnapshot();
        }
      };

      const queued = persistQueueRef.current.then(run);
      persistQueueRef.current = queued;
      void queued.catch(() => {
        if (persistQueueRef.current === queued) {
          persistQueueRef.current = Promise.resolve();
        }
      });
      return queued;
    },
    [updateSettingsConfig]
  );

  return {
    settingsState,
    persistPatch,
    saveError,
    clearSaveError,
    lastSavedAt,
    isLoading: settingsQuery.isLoading,
    isSaving: updateState.isLoading || pendingSaveCount > 0,
  };
};
